import { DialogProps } from "@/types/ui";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormEvent, useEffect } from "react";
import { useForm, usePage } from "@inertiajs/react";
import { AdminPageProps, Product } from "@/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { SearchIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { store_products_path } from "@/lib/routes";
import ImageWithFallback from "./image-with-fallback";
import { Checkbox } from "@/components/ui/checkbox";
import useFilteredProducts from "@/hooks/use-filtered-products";
import useTranslations from "@/hooks/use-translations";
import { getProductTypeLabel } from "@/lib/utils";

type SelectProductsFormData = {
  products: string[];
};

export default function SelectProductsDialog({
  open,
  onOpenChange,
  defaultValue = [],
  onSelect,
}: DialogProps & {
  defaultValue?: string[];
  onSelect: ({ ids, records }: { ids: string[]; records: Product[] }) => void;
}) {
  const page = usePage<AdminPageProps>();
  const { current_store } = page.props;
  const { t } = useTranslations("shared.components.select_products_dialog");

  const productsQuery = useQuery<Product[]>({
    queryKey: [current_store!.id, "products"],
    queryFn() {
      return fetch(store_products_path(current_store!), {
        method: "GET",
        headers: {
          Accept: "text/html, application/xhtml+xml",
          "X-Requested-With": "XMLHttpRequest",
          "X-Inertia": "true",
          "X-Inertia-Version": page.version!,
        },
      })
        .then((response) => response.json())
        .then((data) => data.props.products);
    },
    enabled: !!page.version,
  });

  const { search, setSearch, filteredProducts } = useFilteredProducts(
    productsQuery.data || []
  );

  const productsMap: Record<string, Product> = {};

  productsQuery.data?.forEach((product) => {
    productsMap[product.id] = product;
  });

  const selectProductsForm = useForm<SelectProductsFormData>({
    products: defaultValue,
  });

  const selectAll =
    productsQuery.data &&
    selectProductsForm.data.products.length === productsQuery.data.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      selectProductsForm.setData(
        "products",
        (productsQuery.data || []).map((product) => product.id)
      );
    } else {
      selectProductsForm.setData("products", []);
    }
  };
  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectProductsForm.data.products);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    selectProductsForm.setData("products", Array.from(newSelected));
  };

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const selectedProducts = selectProductsForm.data.products.map(
      (productId) => productsMap[productId]
    );
    onSelect({
      ids: selectProductsForm.data.products,
      records: selectedProducts,
    });
    onOpenChange(false);
  }

  useEffect(() => {
    if (!open && selectProductsForm.isDirty) {
      selectProductsForm.resetAndClearErrors();
    }
  }, [open, selectProductsForm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-10/12 grid-rows-[1fr]">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>

        <form
          id="select-products-form"
          onSubmit={onSubmit}
          className="flex-1 flex flex-col gap-5 overflow-x-hidden overflow-y-auto"
        >
          <InputGroup>
            <InputGroupInput
              placeholder={t("search_placeholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <InputGroupAddon>
              <SearchIcon />
            </InputGroupAddon>
          </InputGroup>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    id="select-all-checkbox"
                    name="select-all-checkbox"
                    checked={selectAll}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead></TableHead>
                <TableHead>{t("shared.fields.name")}</TableHead>
                <TableHead>{t("type_column")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product: Product) => (
                <TableRow
                  key={product.id}
                  data-state={
                    selectProductsForm.data.products.includes(product.id)
                      ? "selected"
                      : undefined
                  }
                >
                  <TableCell>
                    <Checkbox
                      id={`product-${product.id}-checkbox`}
                      name={`product-${product.id}-checkbox`}
                      checked={selectProductsForm.data.products.includes(
                        product.id
                      )}
                      onCheckedChange={(checked) =>
                        handleSelectRow(product.id, checked === true)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <ImageWithFallback
                      srcset={[product.image_url]}
                      alt={product.name}
                      className="size-24 object-contain"
                    />
                  </TableCell>
                  <TableCell>
                    <label htmlFor={`product-${product.id}-checkbox`}>
                      <p className="text-sm font-medium">{product.name}</p>
                      <p className="text-sm">{product.system_code}</p>
                    </label>
                  </TableCell>
                  <TableCell className="capitalize">
                    {getProductTypeLabel(product.product_type, t)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit" form="select-products-form">
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
